import { useEffect, useState } from "react";
import api, { API, apiError } from "../lib/api";
import { PageHeader, Loader, Empty } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { KeyRound, Plus, Trash2, Copy, Check, ShieldOff, ShieldCheck, BookOpen, Terminal } from "lucide-react";
import { toast } from "sonner";

const BASE = `${API}/v1`;

const SCOPE_GROUPS = [
  { label: "Tableau de bord", scopes: [["dashboard:read", "Lecture"]] },
  { label: "Rapports", scopes: [["reports:read", "Lecture"]] },
  { label: "Ventes", scopes: [["sales:read", "Lecture"], ["sales:write", "Écriture"]] },
  { label: "Dépenses", scopes: [["expenses:read", "Lecture"], ["expenses:write", "Écriture"]] },
  { label: "Glovo (journalier)", scopes: [["glovo:read", "Lecture"], ["glovo:write", "Écriture"]] },
  { label: "Produits", scopes: [["products:read", "Lecture"], ["products:write", "Écriture"]] },
  { label: "Finance & Charges fixes", scopes: [["charges:read", "Charges/Salaires (lecture)"], ["charges:write", "Charges/Salaires (écriture)"], ["finance:read", "Indicateurs & trésorerie (lecture)"], ["tresorerie:write", "Trésorerie (écriture)"]] },
  { label: "Stock Laboratoire (ingrédients)", scopes: [["stock:read", "Lecture"], ["stock:write", "Écriture"]] },
];
const ALL_SCOPES = SCOPE_GROUPS.flatMap((g) => g.scopes.map((s) => s[0]));

function ScopeBadge({ s }) {
  const write = s.endsWith(":write");
  return (
    <span
      className="tm-badge"
      style={{
        background: write ? "rgba(212,175,55,.15)" : "rgba(76,175,80,.13)",
        color: write ? "#D4AF37" : "#7Cc47f",
        fontSize: 11,
      }}
    >
      {s}
    </span>
  );
}

function KeysTab() {
  const [keys, setKeys] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState(["dashboard:read"]);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      setKeys((await api.get("/api-keys")).data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const toggleScope = (s) =>
    setScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const create = async () => {
    if (!name.trim()) return toast.error("Nom de la clé requis");
    if (!scopes.length) return toast.error("Sélectionnez au moins une permission");
    try {
      const res = (await api.post("/api-keys", { name: name.trim(), scopes })).data;
      setCreated(res);
      setOpen(false);
      setName("");
      setScopes(["dashboard:read"]);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Révoquer la clé « ${k.name} » ? Elle sera immédiatement refusée.`)) return;
    await api.post(`/api-keys/${k.id}/revoke`);
    toast.success("Clé révoquée");
    load();
  };
  const enable = async (k) => {
    await api.post(`/api-keys/${k.id}/enable`);
    toast.success("Clé réactivée");
    load();
  };
  const remove = async (k) => {
    if (!window.confirm(`Supprimer définitivement la clé « ${k.name} » ?`)) return;
    await api.delete(`/api-keys/${k.id}`);
    toast.success("Clé supprimée");
    load();
  };
  const copyKey = () => {
    navigator.clipboard.writeText(created.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (keys === null) return <Loader />;

  return (
    <div>
      <div className="flex justify-end mb-5">
        <button className="tm-btn tm-btn-gold" onClick={() => setOpen(true)} data-testid="new-api-key-btn">
          <Plus size={16} /> Nouvelle clé API
        </button>
      </div>

      {keys.length === 0 ? (
        <Empty text="Aucune clé API. Créez-en une pour connecter une application externe (Claude, ChatGPT, automatisations)." />
      ) : (
        <div className="tm-card overflow-hidden" data-testid="api-keys-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[var(--tm-muted)] text-xs uppercase tracking-wider" style={{ borderBottom: "1px solid var(--tm-border)" }}>
                  <th className="px-4 py-3">Nom</th>
                  <th className="px-4 py-3">Aperçu</th>
                  <th className="px-4 py-3">Permissions</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3">Dernière utilisation</th>
                  <th className="px-4 py-3">Appels</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} style={{ borderBottom: "1px solid var(--tm-border)" }} data-testid={`api-key-row-${k.id}`}>
                    <td className="px-4 py-3 font-medium">{k.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[var(--tm-muted)]">{k.display_prefix}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(k.scopes || []).map((s) => (
                          <ScopeBadge key={s} s={s} />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="tm-badge" style={{ background: k.enabled ? "rgba(76,175,80,.15)" : "rgba(244,67,54,.15)", color: k.enabled ? "#4CAF50" : "#F44336" }}>
                        {k.enabled ? "Active" : "Révoquée"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[var(--tm-muted)]">
                      {k.last_used ? new Date(k.last_used).toLocaleString("fr-FR") : "—"}
                    </td>
                    <td className="px-4 py-3 tabular">{k.request_count || 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {k.enabled ? (
                          <button className="tm-btn tm-btn-ghost" onClick={() => revoke(k)} title="Révoquer" data-testid={`revoke-key-${k.id}`}>
                            <ShieldOff size={15} />
                          </button>
                        ) : (
                          <button className="tm-btn tm-btn-ghost" onClick={() => enable(k)} title="Réactiver" data-testid={`enable-key-${k.id}`}>
                            <ShieldCheck size={15} />
                          </button>
                        )}
                        <button className="tm-btn tm-btn-ghost" onClick={() => remove(k)} title="Supprimer" data-testid={`delete-key-${k.id}`}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal création */}
      <Modal open={open} onClose={() => setOpen(false)} title="Nouvelle clé API" testid="api-key-modal">
        <Field label="Nom de la clé (ex. « Agent Claude », « Automatisation N8N »)">
          <input className="tm-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Agent Claude" data-testid="api-key-name-input" />
        </Field>
        <div className="mb-2 text-xs uppercase tracking-wider text-[var(--tm-muted)]">Permissions (scopes)</div>
        <div className="space-y-3 mb-4">
          {SCOPE_GROUPS.map((g) => (
            <div key={g.label} className="tm-card p-3" style={{ background: "var(--tm-surface-2)" }}>
              <p className="text-sm font-medium mb-2">{g.label}</p>
              <div className="flex flex-wrap gap-4">
                {g.scopes.map(([s, lbl]) => (
                  <label key={s} className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" checked={scopes.includes(s)} onChange={() => toggleScope(s)} data-testid={`scope-${s}`} />
                    <span>{lbl}</span>
                    <span className="font-mono text-xs text-[var(--tm-muted)]">{s}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <button type="button" className="tm-btn tm-btn-ghost text-xs" onClick={() => setScopes([...ALL_SCOPES])}>Tout cocher</button>
            <button type="button" className="tm-btn tm-btn-ghost text-xs" onClick={() => setScopes([])}>Tout décocher</button>
          </div>
        </div>
        <button className="tm-btn tm-btn-gold w-full" onClick={create} data-testid="create-api-key-submit">
          <KeyRound size={16} /> Générer la clé
        </button>
      </Modal>

      {/* Modal révélation unique */}
      <Modal open={!!created} onClose={() => setCreated(null)} title="Votre clé API — copiez-la maintenant" testid="api-key-created-modal">
        <div className="tm-card p-4 mb-4" style={{ background: "rgba(244,67,54,.08)", border: "1px solid rgba(244,67,54,.3)" }}>
          <p className="text-sm text-[#F5b5b0]">
            ⚠️ Cette clé ne sera affichée <b>qu'une seule fois</b>. Copiez-la et conservez-la en lieu sûr. Vous ne pourrez plus la revoir ensuite.
          </p>
        </div>
        <div className="flex items-center gap-2 mb-4">
          <code className="flex-1 tm-input font-mono text-xs break-all" data-testid="revealed-api-key">{created?.key}</code>
          <button className="tm-btn tm-btn-gold" onClick={copyKey} data-testid="copy-api-key-btn">
            {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copié" : "Copier"}
          </button>
        </div>
        <p className="text-xs text-[var(--tm-muted)]">
          Permissions : {(created?.scopes || []).join(", ")}
        </p>
        <button className="tm-btn tm-btn-ghost w-full mt-4" onClick={() => setCreated(null)}>J'ai copié ma clé</button>
      </Modal>
    </div>
  );
}

function Code({ children }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative">
      <pre className="tm-card p-4 overflow-x-auto text-xs font-mono leading-relaxed" style={{ background: "#0d0d0d", whiteSpace: "pre" }}>
        {children}
      </pre>
      <button onClick={copy} className="absolute top-2 right-2 tm-btn tm-btn-ghost text-xs" title="Copier">
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </div>
  );
}

function Endpoint({ method, path, scope, desc, params, example }) {
  const mColor = { GET: "#4CAF50", POST: "#D4AF37" }[method] || "#A3A39A";
  return (
    <div className="tm-card p-5 mb-4">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <span className="tm-badge font-mono" style={{ background: `${mColor}22`, color: mColor }}>{method}</span>
        <code className="font-mono text-sm">{path}</code>
        <span className="tm-badge" style={{ background: "rgba(212,175,55,.12)", color: "#D4AF37", fontSize: 11 }}>scope : {scope}</span>
      </div>
      <p className="text-sm text-[var(--tm-muted)] mb-3">{desc}</p>
      {params && (
        <div className="mb-3">
          <p className="text-xs uppercase tracking-wider text-[var(--tm-muted)] mb-1.5">Champs</p>
          <ul className="text-xs space-y-1">
            {params.map((p) => (
              <li key={p.name}>
                <code className="font-mono text-[var(--tm-gold)]">{p.name}</code>
                <span className="text-[var(--tm-muted)]"> — {p.req ? "obligatoire" : "facultatif"} · {p.desc}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {example && <Code>{example}</Code>}
    </div>
  );
}

function DocsTab() {
  return (
    <div className="max-w-4xl">
      <div className="tm-card p-5 mb-6">
        <h3 className="font-display text-xl mb-2" style={{ color: "var(--tm-gold)" }}>Démarrage rapide</h3>
        <p className="text-sm text-[var(--tm-muted)] mb-3">
          L'API REST TIRAMESSI expose vos données en lecture et écriture. Elle utilise <b>exactement la même base de données et les mêmes calculs</b> que l'application :
          toute donnée ajoutée via l'API apparaît immédiatement dans le tableau de bord et les rapports.
        </p>
        <p className="text-xs uppercase tracking-wider text-[var(--tm-muted)] mb-1.5">URL de base</p>
        <Code>{BASE}</Code>
        <p className="text-xs uppercase tracking-wider text-[var(--tm-muted)] mt-4 mb-1.5">Authentification (deux méthodes acceptées)</p>
        <Code>{`# Méthode 1 — en-tête X-API-Key
curl "${BASE}/ping" -H "X-API-Key: VOTRE_CLE"

# Méthode 2 — Authorization Bearer
curl "${BASE}/ping" -H "Authorization: Bearer VOTRE_CLE"`}</Code>
        <p className="text-xs text-[var(--tm-muted)] mt-3">
          Chaque clé n'a accès qu'aux permissions (scopes) cochées à sa création. Une requête sans le scope requis renvoie <code>403</code> ;
          une clé absente, invalide ou révoquée renvoie <code>401</code>.
        </p>
      </div>

      <h3 className="font-display text-lg mb-3">Référence · Utilitaires</h3>
      <Endpoint method="GET" path="/api/v1/pos" scope="toute clé valide"
        desc="Liste les points de vente/canaux avec leur ID stable — à utiliser comme pos_id dans les écritures."
        example={`curl "${BASE}/pos" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="GET" path="/api/v1/expense-categories" scope="toute clé valide"
        desc="Liste des catégories de dépenses valides (identiques à l'application)."
        example={`curl "${BASE}/expense-categories" -H "X-API-Key: VOTRE_CLE"`} />

      <h3 className="font-display text-lg mb-3 mt-6">Tableau de bord & rapports</h3>
      <Endpoint method="GET" path="/api/v1/dashboard" scope="dashboard:read"
        desc="Chiffres agrégés strictement identiques au tableau de bord de l'application (CA, bénéfice net, marges, par département…)."
        params={[
          { name: "period", req: false, desc: "today | yesterday | week | month (défaut) | custom" },
          { name: "start, end", req: false, desc: "bornes AAAA-MM-JJ si period=custom" },
        ]}
        example={`curl "${BASE}/dashboard?period=month" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="GET" path="/api/v1/reports" scope="reports:read"
        desc="Rapports agrégés (mensuel ou hebdo), tous points de vente ou filtré."
        params={[
          { name: "periode", req: false, desc: "mensuel (défaut) | hebdo" },
          { name: "pos_id", req: false, desc: "filtrer sur un point de vente" },
        ]}
        example={`curl "${BASE}/reports?periode=mensuel" -H "X-API-Key: VOTRE_CLE"`} />

      <h3 className="font-display text-lg mb-3 mt-6">Ventes</h3>
      <Endpoint method="GET" path="/api/v1/sales" scope="sales:read"
        desc="Liste des ventes journalières."
        params={[
          { name: "pos_id", req: false, desc: "filtrer par point de vente" },
          { name: "limit", req: false, desc: "nombre max (défaut 1000)" },
        ]}
        example={`curl "${BASE}/sales?limit=50" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="POST" path="/api/v1/sales" scope="sales:write"
        desc="Crée une vente rattachée à un point de vente. Le prix et le coût sont auto-remplis depuis le produit si non fournis (marges cohérentes avec l'app)."
        params={[
          { name: "pos_id", req: true, desc: "ID du point de vente (voir GET /pos)" },
          { name: "items[]", req: true, desc: "product_id ou nom, quantite, prix_unitaire (opt.), cout_unitaire (opt.)" },
          { name: "mode_paiement", req: false, desc: "especes (défaut) | carte | virement | glovo" },
          { name: "date", req: false, desc: "AAAA-MM-JJ (défaut : aujourd'hui)" },
        ]}
        example={`curl -X POST "${BASE}/sales" \\
  -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -d '{
    "pos_id": "REMPLACER_PAR_ID_POS",
    "items": [{ "nom": "Cheesecake", "quantite": 3 }],
    "mode_paiement": "especes",
    "date": "2026-06-15"
  }'`} />

      <h3 className="font-display text-lg mb-3 mt-6">Dépenses</h3>
      <Endpoint method="GET" path="/api/v1/expenses" scope="expenses:read"
        desc="Liste des dépenses."
        params={[{ name: "pos_id", req: false, desc: "filtrer par point de vente" }]}
        example={`curl "${BASE}/expenses" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="POST" path="/api/v1/expenses" scope="expenses:write"
        desc="Crée une dépense. La catégorie doit correspondre à une catégorie de l'app (sinon « Divers »)."
        params={[
          { name: "libelle", req: true, desc: "intitulé de la dépense" },
          { name: "montant", req: true, desc: "montant en DH" },
          { name: "pos_id", req: true, desc: "ID du point de vente (voir GET /pos)" },
          { name: "categorie", req: false, desc: "voir GET /expense-categories" },
          { name: "date", req: false, desc: "AAAA-MM-JJ" },
        ]}
        example={`curl -X POST "${BASE}/expenses" \\
  -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -d '{
    "libelle": "Achat emballages",
    "categorie": "Emballages",
    "montant": 250,
    "pos_id": "REMPLACER_PAR_ID_POS",
    "date": "2026-06-15"
  }'`} />

      <Endpoint method="PUT" path="/api/v1/expenses/{id}" scope="expenses:write"
        desc="Modifie EN PLACE une dépense existante (jamais de nouvelle dépense). Mise à jour partielle : n'envoyez que les champs à changer. Les dashboards, coûts variables et résultats sont actualisés. Le lien avec une charge fixe / un paiement de salaire est préservé (montant du paiement synchronisé). En-tête optionnel Idempotency-Key."
        params={[
          { name: "id (URL)", req: true, desc: "ID de la dépense" },
          { name: "montant", req: false, desc: "nombre > 0 (DH)" },
          { name: "categorie", req: false, desc: "voir GET /expense-categories" },
          { name: "libelle", req: false, desc: "libellé non vide" },
          { name: "date", req: false, desc: "AAAA-MM-JJ" },
          { name: "pos_id", req: false, desc: "point de vente (voir GET /pos)" },
          { name: "mode_paiement", req: false, desc: "especes | carte | virement…" },
          { name: "fournisseur, commentaire", req: false, desc: "texte" },
        ]}
        example={`curl -X PUT "${BASE}/expenses/EXPENSE_ID" \\
  -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -H "Idempotency-Key: mcp-exp-2026-10-19-001" \\
  -d '{ "montant": 275, "categorie": "Emballages" }'`} />

      <h3 className="font-display text-lg mb-3 mt-6">Glovo (saisie journalière)</h3>
      <Endpoint method="GET" path="/api/v1/glovo" scope="glovo:read"
        desc="Liste des saisies journalières Glovo (CA brut, commission, CA net)."
        example={`curl "${BASE}/glovo" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="POST" path="/api/v1/glovo" scope="glovo:write"
        desc="Enregistre une saisie journalière. Commission et CA net calculés automatiquement. Une seule saisie par date : un envoi sur une date existante la met à jour (anti-doublon garanti)."
        params={[
          { name: "date", req: true, desc: "AAAA-MM-JJ" },
          { name: "ca_brut", req: true, desc: "chiffre d'affaires brut Glovo en DH" },
          { name: "commission_pct", req: false, desc: "défaut 24 (%)" },
          { name: "nb_commandes", req: false, desc: "nombre de commandes" },
        ]}
        example={`curl -X POST "${BASE}/glovo" \\
  -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -d '{
    "date": "2026-06-15",
    "ca_brut": 1200,
    "commission_pct": 24
  }'`} />

      <h3 className="font-display text-lg mb-3 mt-6">Produits</h3>
      <Endpoint method="GET" path="/api/v1/products" scope="products:read"
        desc="Liste des produits avec prix, coût de revient et marges calculés."
        example={`curl "${BASE}/products" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="POST" path="/api/v1/products" scope="products:write"
        desc="Crée un produit. Coût de revient et marges auto-calculés depuis la recette et les prix d'ingrédients du Laboratoire. Apparaît immédiatement dans Produits & Marges."
        params={[
          { name: "nom", req: true, desc: "nom du produit" },
          { name: "prix_vente", req: true, desc: "prix de vente > 0 (DH)" },
          { name: "categorie", req: false, desc: "défaut « Dessert »" },
          { name: "cout_emballage", req: false, desc: "coût d'emballage par unité ≥ 0" },
          { name: "cout_matiere", req: false, desc: "utilisé seulement si pas de recette (≥ 0)" },
          { name: "description, image", req: false, desc: "texte / URL image" },
          { name: "actif", req: false, desc: "booléen (défaut true)" },
          { name: "recette[]", req: false, desc: "{ stock_item_id, nom, quantite, unite } — ingrédients du Labo pour le coût de revient" },
        ]}
        example={`curl -X POST "${BASE}/products" \\
  -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -d '{
    "nom": "Tiramisu Classique",
    "categorie": "Dessert",
    "prix_vente": 45,
    "cout_emballage": 2,
    "actif": true,
    "recette": [
      { "nom": "Mascarpone", "quantite": 0.08, "unite": "kg" }
    ]
  }'`} />
      <Endpoint method="PUT" path="/api/v1/products/{id}" scope="products:write"
        desc="Modifie un produit existant (mêmes champs que la création). Renvoie 404 si l'ID n'existe pas."
        params={[
          { name: "id (URL)", req: true, desc: "ID du produit (voir GET /products)" },
          { name: "corps", req: true, desc: "mêmes champs que POST /products" },
        ]}
        example={`curl -X PUT "${BASE}/products/PRODUCT_ID" \\
  -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -d '{ "nom": "Tiramisu Classique", "prix_vente": 50, "cout_emballage": 2 }'`} />

      <h3 className="font-display text-lg mb-3 mt-6">Finance & Charges fixes</h3>
      <Endpoint method="GET" path="/api/v1/finance" scope="finance:read"
        desc="Indicateurs de pilotage du mois (CA net, marge brute, charges fixes provisionnées/payées/restantes, résultat après charges, prévisionnel, seuil de rentabilité). Identiques au tableau de bord."
        params={[
          { name: "year, month", req: false, desc: "mois ciblé (défaut : mois courant)" },
        ]}
        example={`curl "${BASE}/finance?year=2026&month=10" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="GET" path="/api/v1/charges-fixes" scope="charges:read"
        desc="Liste des charges fixes actives (nom, montant mensuel, catégorie, point de vente, prochaine échéance)."
        example={`curl "${BASE}/charges-fixes" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="GET" path="/api/v1/salaries" scope="charges:read"
        desc="Liste des salariés avec salaire mensuel, montant versé ce mois et reste à verser."
        example={`curl "${BASE}/salaries" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="GET" path="/api/v1/charges-fixes/categories" scope="charges:read"
        desc="Catégories de charges autorisées."
        example={`curl "${BASE}/charges-fixes/categories" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="GET" path="/api/v1/charges-fixes/{id}/historique" scope="charges:read"
        desc="Historique des changements de montant d'une charge (ancienne/nouvelle valeur, date d'effet, auteur)."
        example={`curl "${BASE}/charges-fixes/CHARGE_ID/historique" -H "X-API-Key: VOTRE_CLE"`} />

      <h3 className="font-display text-lg mb-3 mt-6">Trésorerie & relevés Glovo</h3>
      <Endpoint method="GET" path="/api/v1/tresorerie" scope="finance:read"
        desc="Carte Trésorerie : espèces en caisse et banque théoriques, Glovo encaissé / à recevoir (montant + date), trésorerie totale, charges fixes restant à payer, trésorerie après charges, flux de la période."
        params={[{ name: "date_from, date_to", req: false, desc: "YYYY-MM-DD (défaut : début du mois → aujourd'hui)" }]}
        example={`curl "${BASE}/tresorerie?date_from=2026-10-01&date_to=2026-10-15" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="GET" path="/api/v1/glovo/releves" scope="finance:read" desc="Relevés de quinzaine Glovo (net, espèces collectées, virement attendu, statut)." example={`curl "${BASE}/glovo/releves" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="POST" path="/api/v1/glovo/releves" scope="tresorerie:write"
        desc="Saisit un relevé de quinzaine. Commission = taux × (ventes − promotions) ; net = ventes − promotions − commission − frais ; virement = net − espèces. ?dry_run=true = aperçu sans écriture. En-tête Idempotency-Key."
        params={[{ name: "date_debut, date_fin, ventes_brutes", req: true, desc: "période et ventes brutes" }, { name: "promotions, commission_pct, commission, frais_livraison_promo, frais_incidents, frais_attente, especes_collectees, virement_attendu, date_virement", req: false, desc: "montants du relevé" }]}
        example={`curl -X POST "${BASE}/glovo/releves?dry_run=true" -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -d '{ "date_debut": "2026-10-01", "date_fin": "2026-10-15", "ventes_brutes": 9024, "promotions": 717.55, "frais_livraison_promo": 56, "frais_incidents": 100, "frais_attente": 101.4, "especes_collectees": 3704.65, "date_virement": "2026-11-04" }'`} />
      <Endpoint method="POST" path="/api/v1/glovo/releves/{id}/recu" scope="tresorerie:write" desc="Marque le virement comme reçu (date_reception, montant_recu). dry_run + Idempotency-Key." example={`curl -X POST "${BASE}/glovo/releves/ID/recu" -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" -d '{ "date_reception": "2026-11-04", "montant_recu": 2350.85 }'`} />
      <Endpoint method="GET / POST" path="/api/v1/tresorerie/mouvements" scope="finance:read / tresorerie:write" desc="Mouvements internes : type = depot_banque | retrait_banque | apport_gerant | retrait_gerant ; montant, date, compte (caisse|banque, pour le gérant). dry_run + Idempotency-Key." example={`curl -X POST "${BASE}/tresorerie/mouvements" -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" -d '{ "type": "depot_banque", "montant": 2000, "date": "2026-10-20" }'`} />
      <Endpoint method="GET / POST" path="/api/v1/tresorerie/comptages" scope="finance:read / tresorerie:write" desc="Comptage de caisse : montant_compte, date, commentaire → enregistre le théorique et l'écart. dry_run + Idempotency-Key." example={`curl -X POST "${BASE}/tresorerie/comptages?dry_run=true" -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" -d '{ "montant_compte": 3500, "date": "2026-10-20" }'`} />
      <Endpoint method="GET / PUT" path="/api/v1/tresorerie/parametres" scope="finance:read / tresorerie:write" desc="Soldes de départ : date_depart, solde_caisse, solde_banque, soldes_renseignes (false = la carte n'affiche que les mouvements du mois). dry_run + Idempotency-Key." example={`curl -X PUT "${BASE}/tresorerie/parametres?dry_run=true" -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" -d '{ "date_depart": "2026-10-01", "solde_caisse": 0, "solde_banque": 0, "soldes_renseignes": false }'`} />
      <Endpoint method="POST / DELETE" path="/api/v1/expenses/{id}/rattachement" scope="charges:write" desc="Rattache une dépense à une charge fixe et/ou un salarié (charge_id, salarie_id, salary_payment_id, sync_montant) ou supprime le rattachement (DELETE). Le montant n'est jamais modifié sauf sync_montant=true. dry_run + Idempotency-Key." example={`curl -X POST "${BASE}/expenses/ID/rattachement?dry_run=true" -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" -d '{ "salarie_id": "ID_SALARIE" }'`} />
      <Endpoint method="PUT" path="/api/v1/salaries/{id}/date-entree" scope="charges:write" desc="Modifie la date d'entrée d'un salarié et la date de début de sa charge fixe, sans toucher aux paiements. dry_run + Idempotency-Key." example={`curl -X PUT "${BASE}/salaries/ID/date-entree?dry_run=true" -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" -d '{ "date_entree": "2026-10-01" }'`} />
      <Endpoint method="PUT" path="/api/v1/glovo/releves/{id} · POST /api/v1/tresorerie/mouvements/{id}/annuler" scope="tresorerie:write" desc="Modifier un relevé Glovo · annuler un mouvement interne (jamais de suppression). dry_run + Idempotency-Key." example={`curl -X POST "${BASE}/tresorerie/mouvements/ID/annuler?dry_run=true" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="GET" path="/api/v1/finance/rattachements" scope="finance:read" desc="Dépenses proposées au rattachement à une charge fixe (à valider dans l'app)." example={`curl "${BASE}/finance/rattachements" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="GET" path="/api/v1/finance/ventes-sans-cout" scope="finance:read" desc="Lignes de vente sans product_id ou sans coût (libellé, quantité, CA)." example={`curl "${BASE}/finance/ventes-sans-cout?year=2026&month=10" -H "X-API-Key: VOTRE_CLE"`} />

      <div className="tm-card p-4 mb-4" style={{ background: "rgba(212,175,55,.08)", border: "1px solid rgba(212,175,55,.3)" }}>
        <p className="text-sm text-[var(--tm-beige)]">
          ✍️ <b>Écritures (scope <code>charges:write</code>)</b> — toutes les créations/modifications ci-dessous acceptent un en-tête optionnel
          <code> Idempotency-Key</code> : renvoyer le même appel avec la même clé ne crée pas de doublon (idempotence pour les reprises MCP).
          Chaque écriture est journalisée (audit). La <b>suppression via l'API est un archivage</b> (jamais de suppression définitive) — l'historique est préservé.
        </p>
      </div>
      <Endpoint method="POST" path="/api/v1/charges-fixes" scope="charges:write"
        desc="Crée une charge fixe. En-tête optionnel Idempotency-Key."
        params={[
          { name: "nom", req: true, desc: "nom de la charge" },
          { name: "montant", req: true, desc: "montant mensuel > 0 (DH)" },
          { name: "categorie", req: false, desc: "voir /charges-fixes/categories (défaut Divers)" },
          { name: "pos_id", req: false, desc: "point de vente, vide = commune entreprise" },
          { name: "salarie_id", req: false, desc: "salarié associé" },
          { name: "jour_paiement", req: false, desc: "1-31 (défaut 1)" },
          { name: "date_debut", req: false, desc: "AAAA-MM-JJ (défaut 1er du mois)" },
          { name: "date_fin", req: false, desc: "AAAA-MM-JJ facultatif" },
        ]}
        example={`curl -X POST "${BASE}/charges-fixes" \\
  -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -H "Idempotency-Key: mcp-2026-10-19-001" \\
  -d '{ "nom": "Assurance", "montant": 600, "categorie": "Divers", "jour_paiement": 5, "date_debut": "2026-10-01" }'`} />
      <Endpoint method="PUT" path="/api/v1/charges-fixes/{id}" scope="charges:write"
        desc="Modifie une charge (nom, catégorie, POS, salarié, jour/dates, statut). Pour changer le MONTANT avec effet daté, utilisez /montant (préserve les bilans passés)."
        example={`curl -X PUT "${BASE}/charges-fixes/CHARGE_ID" \\
  -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -d '{ "nom": "Loyer", "montant": 4500, "categorie": "Loyer" }'`} />
      <Endpoint method="POST" path="/api/v1/charges-fixes/{id}/montant" scope="charges:write"
        desc="Change le montant à partir d'une date d'effet, SANS modifier les périodes/bilans passés. Historisé. Idempotency-Key supporté."
        params={[
          { name: "montant", req: true, desc: "nouveau montant > 0" },
          { name: "date_effet", req: false, desc: "AAAA-MM-JJ (défaut aujourd'hui)" },
        ]}
        example={`curl -X POST "${BASE}/charges-fixes/CHARGE_ID/montant" \\
  -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -d '{ "montant": 5000, "date_effet": "2026-11-01" }'`} />
      <Endpoint method="POST" path="/api/v1/charges-fixes/{id}/desactiver" scope="charges:write"
        desc="Désactive la charge (stoppe les provisions à partir d'aujourd'hui)."
        example={`curl -X POST "${BASE}/charges-fixes/CHARGE_ID/desactiver" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="POST" path="/api/v1/charges-fixes/{id}/reactiver" scope="charges:write"
        desc="Réactive une charge désactivée."
        example={`curl -X POST "${BASE}/charges-fixes/CHARGE_ID/reactiver" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="DELETE" path="/api/v1/charges-fixes/{id}" scope="charges:write"
        desc="ARCHIVE la charge (jamais de suppression définitive via l'API). L'historique est préservé."
        example={`curl -X DELETE "${BASE}/charges-fixes/CHARGE_ID" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="POST" path="/api/v1/salaries" scope="charges:write"
        desc="Crée un salarié. Idempotency-Key supporté."
        params={[
          { name: "nom", req: true, desc: "nom du salarié" },
          { name: "salaire_mensuel", req: true, desc: "> 0 (DH)" },
          { name: "fonction", req: false, desc: "poste" },
          { name: "pos_id", req: false, desc: "point de vente" },
        ]}
        example={`curl -X POST "${BASE}/salaries" \\
  -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -d '{ "nom": "Yassine", "fonction": "Vendeur", "salaire_mensuel": 2800 }'`} />
      <Endpoint method="PUT" path="/api/v1/salaries/{id}" scope="charges:write"
        desc="Modifie un salarié. Un changement de salaire est historisé avec date d'effet (anciens montants conservés)."
        example={`curl -X PUT "${BASE}/salaries/SALARY_ID" \\
  -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -d '{ "nom": "Yassine", "salaire_mensuel": 3000 }'`} />
      <Endpoint method="DELETE" path="/api/v1/salaries/{id}" scope="charges:write"
        desc="ARCHIVE un salarié (jamais de suppression définitive via l'API)."
        example={`curl -X DELETE "${BASE}/salaries/SALARY_ID" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="POST" path="/api/v1/salaries/{id}/paiements" scope="charges:write"
        desc="Enregistre une avance ou un paiement de salaire. Crée aussi une dépense liée (catégorie Salaires) si lier_depense=true. Une avance réduit le reste à verser sans réduire la charge mensuelle. Idempotency-Key supporté."
        params={[
          { name: "type", req: false, desc: "avance (défaut) | salaire" },
          { name: "montant", req: true, desc: "> 0 (DH)" },
          { name: "date", req: false, desc: "AAAA-MM-JJ" },
          { name: "mode_paiement", req: false, desc: "especes (défaut) | carte | virement" },
          { name: "lier_depense", req: false, desc: "booléen (défaut true) — reflète la sortie de trésorerie" },
        ]}
        example={`curl -X POST "${BASE}/salaries/SALARY_ID/paiements" \\
  -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -H "Idempotency-Key: mcp-pay-2026-10-19-001" \\
  -d '{ "type": "avance", "montant": 500, "date": "2026-10-19" }'`} />

      <h3 className="font-display text-lg mb-3 mt-6">Stock Laboratoire (ingrédients)</h3>
      <Endpoint method="GET" path="/api/v1/stock" scope="stock:read"
        desc="Liste des ingrédients réels du Stock Laboratoire (même collection que la page Stock)."
        example={`curl "${BASE}/stock" -H "X-API-Key: VOTRE_CLE"`} />
      <Endpoint method="POST" path="/api/v1/stock" scope="stock:write"
        desc="Crée un ingrédient dans le Stock Laboratoire. Il apparaît immédiatement sur la page /stocks (même base de données)."
        params={[
          { name: "nom", req: true, desc: "nom de l'ingrédient" },
          { name: "unite", req: true, desc: "unité (kg, g, l, ml, unité…)" },
          { name: "quantite", req: false, desc: "nombre ≥ 0 (défaut 0)" },
          { name: "seuil_alerte", req: false, desc: "nombre ≥ 0 (défaut 0)" },
          { name: "cout_unitaire", req: false, desc: "coût d'achat unitaire ≥ 0 (défaut 0)" },
          { name: "fournisseur", req: false, desc: "nom du fournisseur" },
        ]}
        example={`curl -X POST "${BASE}/stock" \\
  -H "X-API-Key: VOTRE_CLE" -H "Content-Type: application/json" \\
  -d '{
    "nom": "Mascarpone",
    "quantite": 10,
    "unite": "kg",
    "seuil_alerte": 2,
    "cout_unitaire": 60,
    "fournisseur": "Fournisseur X"
  }'`} />
    </div>
  );
}

export default function ApiSettings() {
  const [tab, setTab] = useState("keys");
  return (
    <div data-testid="api-settings-page">
      <PageHeader title="API & Intégrations" subtitle="Connectez TIRAMESSI Manager à des agents IA et automatisations externes via une API REST sécurisée." />
      <div className="flex gap-2 mb-6">
        <button
          className={`tm-btn ${tab === "keys" ? "tm-btn-gold" : "tm-btn-ghost"}`}
          onClick={() => setTab("keys")}
          data-testid="tab-keys"
        >
          <KeyRound size={16} /> Clés API
        </button>
        <button
          className={`tm-btn ${tab === "docs" ? "tm-btn-gold" : "tm-btn-ghost"}`}
          onClick={() => setTab("docs")}
          data-testid="tab-docs"
        >
          <BookOpen size={16} /> Documentation
        </button>
      </div>
      {tab === "keys" ? <KeysTab /> : <DocsTab />}
    </div>
  );
}
